/**
 * queue-hours — shared opening-hours vocabulary for queues (used by the
 * Queues list, the queue wizard and the seeds' shapes).
 */
import type { QueueHours } from "./queues-api";
import { t as i18nT, currentLocale } from "../../lib/i18n";

// ui-config: day identities (stored hours index them, NewJourneys matches them against en-US weekday parts); qDayLabel is what shows
export const Q_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A Q_DAYS index as the viewer's language names the day. 2024-01-01 was a Monday. */
export function qDayLabel(index: number): string {
  const day = new Date(Date.UTC(2024, 0, 1 + index));
  try { return new Intl.DateTimeFormat(currentLocale(), { weekday: "short", timeZone: "UTC" }).format(day); }
  catch { return Q_DAYS[index] ?? ""; }
}

/** "13:30" as a time of day. English keeps the house format ("1:30 PM", "9 AM");
 *  every other language reads it on its own clock ("13:30"). */
export function hmLabel12(hm: string): string {
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  const locale = currentLocale();
  if (!/^en\b/i.test(locale)) {
    try { return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone: "UTC" }).format(new Date(Date.UTC(2024, 0, 1, h || 0, m || 0))); }
    catch { /* an unknown locale keeps the house format */ }
  }
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${ap}` : `${h12} ${ap}`;
}

export const TIME_OPTS: string[] = (() => {
  const out: string[] = [];
  for (let h = 6; h <= 22; h++) { out.push(`${String(h).padStart(2, "0")}:00`); if (h < 22) out.push(`${String(h).padStart(2, "0")}:30`); }
  return out;
})();

/** "America/Chicago" → "Chicago time". The IANA path is a database key; the
 *  city is what a person recognises. "" when there is no zone to name. */
export function zoneLabel(timeZone?: string | null): string {
  const tz = String(timeZone || "").trim();
  if (!tz) return "";
  const city = tz.split("/").pop()?.replace(/_/g, " ") || tz;
  return i18nT("queues.queueHours.zoneTime", "{city} time", { city });
}

/** "Mon 10 AM–2 PM · Thu–Sat 9 AM–5 PM" — consecutive days with one window collapse to a range.
 *
 *  `authored` is the queue's `hoursAuthored` bit. Hours that were never
 *  authored do NOT govern — the queue worker applies a desk's own hours only
 *  when `hoursAuthored === true` — so showing "Mon–Fri 9 AM–5 PM" for a queue
 *  carrying `qBase()`'s untouched default told operators a schedule that is not
 *  in force (CD, 2026-09-17).
 *
 *  The test is `!== true`, matching the worker's gate. It was `=== false`,
 *  which nothing ever writes — the bit is three-valued and an untouched queue
 *  carries `undefined` — so the honest label never appeared and the fix was
 *  inert. Every live caller passes the bit; a caller that omits it is treated
 *  as unauthored, which is the safe reading (it says the location governs,
 *  which is what the worker does when the bit is absent).
 *
 *  `opts` names the clock and the override (CD, 2026-09-18):
 *   · `timeZone` — the LOCATION's IANA zone. Appended as "· Chicago time",
 *     because an operator reading "9 AM–5 PM" from another office has no way to
 *     know whose 9 AM it is, and a desk's hours are always the venue's clock.
 *   · `ignoreHours` — the desk takes joins outside its opening hours, so no
 *     schedule is in force and the label must not imply one. It outranks both
 *     the hours and `authored`.
 *  Both optional; omitting them gives the previous reading exactly. */
export function qHoursLabel(
  hours: QueueHours[] | undefined,
  authored?: boolean,
  opts?: { timeZone?: string | null; ignoreHours?: boolean },
): string {
  if (opts?.ignoreHours) return i18nT("queues.queueHours.anyTime", "Takes joins any time — opening hours ignored");
  const zone = zoneLabel(opts?.timeZone);
  if (!hours || hours.length === 0) return i18nT("queues.queueHours.followsLocation", "Follows location hours");
  if (authored !== true) return i18nT("queues.queueHours.followsLocation", "Follows location hours");
  const windows = hours.map((h) => {
    if (!Array.isArray(h.days) || h.days.length === 0) return "—";
    const days = [...h.days].sort((a, b) => a - b);
    const parts: string[] = [];
    let s = 0;
    while (s < days.length) {
      let e = s;
      while (e + 1 < days.length && days[e + 1] === days[e] + 1) e++;
      parts.push(e > s ? `${qDayLabel(days[s]!)}–${qDayLabel(days[e]!)}` : qDayLabel(days[s]!));
      s = e + 1;
    }
    return `${parts.join(", ")} ${hmLabel12(h.start)}–${hmLabel12(h.end)}`;
  }).join(" · ");
  return zone ? `${windows} · ${zone}` : windows;
}
